import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { type Address, formatEther, getAddress, type Hex, parseUnits } from 'viem'
import { DepositModal } from '@/components/deposit/DepositModal'
import { publicConfig } from '@/config/publicConfig'
import {
  getLedgerBalancesSelf,
  getLedgerBalancesSelfQueryKey,
  useGetUserSelf,
  useListAssets,
} from '@/hooks/v1'
import { getDepositInstructions, listDeposits } from '@/services/web3/depositApi'
import { isWeb3DepositAvailable } from '@/services/web3/depositAvailability'
import {
  encodeDepositCalldata,
  erc20DepositAbi,
  USDC_DECIMALS,
  validateDepositInstructions,
  validateUsdcAmount,
} from '@/services/web3/depositContract'
import {
  claimDepositNotification,
  clearPendingDeposit,
  type DepositNotificationOutcome,
  loadPendingDeposits,
  type PendingDepositRecord,
  savePendingDeposit,
} from '@/services/web3/depositPersistence'
import { isExpectedDepositEligibilityError } from '@/services/web3/depositTelemetry'
import {
  connectMetaMask,
  createMetaMaskClients,
  disconnectMetaMask,
  discoverMetaMask,
  type EIP1193Provider,
  getConnectedAccounts,
  getProviderChainId,
  normalizeWalletError,
  signPermitTypedData,
  subscribeToMetaMask,
  switchProviderChain,
} from '@/services/web3/metamaskProvider'
import { isWeb3WithdrawalAvailable } from '@/services/web3/withdrawalAvailability'
import { getApiKey } from '@/utils/apiKeyAuth'
import { useFirebaseAuth } from '@/utils/firebaseAuthProvider'
import { createNotification, NotificationStatus, NotificationType } from '@/utils/notificationUtils'

export type DepositPhase =
  | 'disconnected'
  | 'wrong-network'
  | 'deposit-form'
  | 'preparing'
  | 'wallet-confirm-permit'
  | 'wallet-confirm-tx'
  | 'tx-pending'
  | 'credit-pending'
  | 'completed'
  | 'error'

export type DepositError = {
  title: string
  message: string
  terminal?: boolean
}

type DepositContextValue = {
  available: boolean
  withdrawalsAvailable: boolean
  openDeposit: () => void
}

export type DepositModalModel = {
  open: boolean
  setOpen: (open: boolean) => void
  phase: DepositPhase
  account: Address | null
  chainId: number | null
  walletAvailable: boolean | null
  disconnecting: boolean
  usdcBalance: bigint | null
  ethBalance: bigint | null
  estimatedMaximumFee: bigint | null
  amount: string
  setAmount: (amount: string) => void
  amountError: string | null
  txHash: Hex | null
  error: DepositError | null
  connect: () => Promise<void>
  disconnect: () => Promise<void>
  switchNetwork: () => Promise<void>
  submit: () => Promise<void>
  retry: () => void
  resume: () => Promise<void>
  reset: () => void
}

const DepositContext = createContext<DepositContextValue>({
  available: false,
  withdrawalsAvailable: false,
  openDeposit: () => undefined,
})

const addressesEqual = (left: string, right: string) => left.toLowerCase() === right.toLowerCase()
const delay = (milliseconds: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds))

export const DepositProvider = ({ children }: PropsWithChildren) => {
  const config = publicConfig.web3Deposit
  const queryClient = useQueryClient()
  const firebaseUser = useFirebaseAuth()
  const authenticated = Boolean(firebaseUser || getApiKey())
  const userQuery = useGetUserSelf({ query: { enabled: authenticated } })
  const user = userQuery.data?.data
  const assetsQuery = useListAssets({}, { query: { enabled: authenticated } })
  const assets = assetsQuery.data?.data ?? []
  const withdrawalsAvailable = authenticated && isWeb3WithdrawalAvailable(user, assets)
  const available = authenticated && isWeb3DepositAvailable(user, assets)

  const [open, setOpen] = useState(false)
  const [phase, setPhase] = useState<DepositPhase>('disconnected')
  const [provider, setProvider] = useState<EIP1193Provider | null>(null)
  const [walletAvailable, setWalletAvailable] = useState<boolean | null>(null)
  const [disconnecting, setDisconnecting] = useState(false)
  const [account, setAccount] = useState<Address | null>(null)
  const [chainId, setChainId] = useState<number | null>(null)
  const [usdcBalance, setUsdcBalance] = useState<bigint | null>(null)
  const [ethBalance, setEthBalance] = useState<bigint | null>(null)
  const [estimatedMaximumFee, setEstimatedMaximumFee] = useState<bigint | null>(null)
  const [amount, setAmount] = useState('')
  const [txHash, setTxHash] = useState<Hex | null>(null)
  const [error, setError] = useState<DepositError | null>(null)
  const openRef = useRef(open)
  // Multiple deposits can reconcile in the background, but only the foreground hash drives the modal.
  const trackingHashesRef = useRef(new Set<Hex>())
  const foregroundHashRef = useRef<Hex | null>(null)
  const submissionInFlightRef = useRef(false)
  const trackerGenerationRef = useRef(0)
  const mountedRef = useRef(true)

  useEffect(() => {
    openRef.current = open
  }, [open])

  const notifyDepositOutcome = useCallback(
    (
      record: PendingDepositRecord,
      outcome: DepositNotificationOutcome,
      title: string,
      message: string,
      status: NotificationStatus,
    ) => {
      // The open modal already shows this deposit's outcome; toast only background outcomes.
      if (!record.txHash || (openRef.current && foregroundHashRef.current === record.txHash)) return
      if (!claimDepositNotification(record.txHash, outcome)) return
      createNotification(title, message, NotificationType.SIMPLE, status)
    },
    [],
  )

  const clearWalletConnection = useCallback(() => {
    setAccount(null)
    setChainId(null)
    setUsdcBalance(null)
    setEthBalance(null)
    setEstimatedMaximumFee(null)
    setAmount('')
    setError(null)
    setPhase((current) =>
      ['tx-pending', 'credit-pending'].includes(current) ? current : 'disconnected',
    )
  }, [])

  const refreshBalances = useCallback(
    async (selectedProvider: EIP1193Provider, selectedAccount: Address) => {
      const { publicClient } = createMetaMaskClients(selectedProvider, config.chainId)
      const usdcAddress = getAddress(config.usdcAddress)
      const [decimals, tokenBalance, nativeBalance] = await Promise.all([
        publicClient.readContract({
          address: usdcAddress,
          abi: erc20DepositAbi,
          functionName: 'decimals',
        }),
        publicClient.readContract({
          address: usdcAddress,
          abi: erc20DepositAbi,
          functionName: 'balanceOf',
          args: [selectedAccount],
        }),
        publicClient.getBalance({ address: selectedAccount }),
      ])
      if (decimals !== USDC_DECIMALS) throw new Error(`Configured token uses ${decimals} decimals`)
      if (!mountedRef.current) return
      setUsdcBalance(tokenBalance)
      setEthBalance(nativeBalance)
    },
    [config.chainId, config.usdcAddress],
  )

  const settleWalletState = useCallback(
    async (selectedProvider: EIP1193Provider, selectedAccount: Address | null) => {
      const selectedChainId = await getProviderChainId(selectedProvider)
      if (!mountedRef.current) return
      setAccount(selectedAccount)
      setChainId(selectedChainId)
      if (!selectedAccount) {
        clearWalletConnection()
        return
      }
      if (selectedChainId !== config.chainId) {
        setPhase((current) =>
          ['tx-pending', 'credit-pending', 'completed'].includes(current)
            ? current
            : 'wrong-network',
        )
        return
      }
      await refreshBalances(selectedProvider, selectedAccount)
      setPhase((current) =>
        [
          'wallet-confirm-permit',
          'wallet-confirm-tx',
          'preparing',
          'tx-pending',
          'credit-pending',
          'completed',
        ].includes(current)
          ? current
          : 'deposit-form',
      )
    },
    [clearWalletConnection, config.chainId, refreshBalances],
  )

  const fail = useCallback((nextError: DepositError, capturedError?: unknown) => {
    setError(nextError)
    setPhase('error')
    if (capturedError && !isExpectedDepositEligibilityError(capturedError)) {
      Sentry.captureMessage('Web3 deposit flow failed', {
        level: 'error',
        tags: { feature: 'web3-deposit' },
        extra: {
          title: nextError.title,
          errorName: capturedError instanceof Error ? capturedError.name : 'UnknownError',
          errorMessage: capturedError instanceof Error ? capturedError.message : 'Unknown error',
        },
      })
    }
  }, [])

  const findDepositByHash = useCallback(
    async (record: PendingDepositRecord, signal?: AbortSignal) => {
      if (!record.txHash) return null
      const cutoff =
        new Date(record.createdAt).getTime() - config.depositLookupLookbackSeconds * 1000
      for (let page = 1; page <= config.depositLookupMaxPages; page += 1) {
        const deposits = await listDeposits({
          userId: record.userId,
          page,
          limit: config.depositLookupPageSize,
          signal,
        })
        const match = deposits.find((deposit) => addressesEqual(deposit.tx_hash, record.txHash!))
        if (match) return match
        if (deposits.length < config.depositLookupPageSize) return null
        const oldest = deposits.at(-1)?.observed_at
        if (oldest && new Date(oldest).getTime() < cutoff) return null
      }
      return null
    },
    [
      config.depositLookupLookbackSeconds,
      config.depositLookupMaxPages,
      config.depositLookupPageSize,
    ],
  )

  const reconcileDeposit = useCallback(
    async (record: PendingDepositRecord, generation: number) => {
      let attempt = 0
      while (mountedRef.current && trackerGenerationRef.current === generation) {
        try {
          const deposit = await findDepositByHash(record)
          if (!mountedRef.current || trackerGenerationRef.current !== generation) return
          if (deposit) {
            let quantitiesMatch = false
            try {
              quantitiesMatch =
                parseUnits(deposit.quantity, USDC_DECIMALS) ===
                parseUnits(record.quantity, USDC_DECIMALS)
            } catch {}
            // A matching hash alone is insufficient: verify that the indexed record describes this deposit.
            const consistent =
              addressesEqual(deposit.tx_hash, record.txHash ?? '') &&
              deposit.user_id.toLowerCase() === record.userId.toLowerCase() &&
              addressesEqual(deposit.depositor_address, record.walletAddress) &&
              addressesEqual(deposit.contract_address, config.vaultAddress) &&
              deposit.chain_id === String(record.chainId) &&
              quantitiesMatch
            if (!consistent) {
              trackingHashesRef.current.delete(record.txHash!)
              notifyDepositOutcome(
                record,
                'verification-failed',
                'Deposit needs attention',
                'The indexed deposit did not match the submitted transaction. Contact support with the transaction hash.',
                NotificationStatus.ERROR,
              )
              if (foregroundHashRef.current === record.txHash)
                fail({
                  title: 'Deposit record could not be verified',
                  message:
                    'The indexed record does not match the submitted deposit. Keep the transaction hash and contact support before retrying.',
                })
              return
            }

            // Chain confirmation is not ledger credit. Complete only once credit exists and balances refresh.
            if (deposit.status === 'FINALIZED' && deposit.transaction_id) {
              const refreshedBalances = await getLedgerBalancesSelf()
              if (!mountedRef.current || trackerGenerationRef.current !== generation) return
              queryClient.setQueryData(getLedgerBalancesSelfQueryKey(), refreshedBalances)
              clearPendingDeposit(record.txHash!)
              trackingHashesRef.current.delete(record.txHash!)
              if (foregroundHashRef.current === record.txHash) setPhase('completed')
              notifyDepositOutcome(
                record,
                'credited',
                'Deposit credited',
                `${record.quantity} USD has been credited to your DORA balance.`,
                NotificationStatus.SUCCESS,
              )
              return
            }
            if (deposit.status === 'ORPHANED') {
              clearPendingDeposit(record.txHash!)
              trackingHashesRef.current.delete(record.txHash!)
              notifyDepositOutcome(
                record,
                'orphaned',
                'Deposit was orphaned',
                'The deposit event is no longer canonical. Contact support with the transaction hash.',
                NotificationStatus.ERROR,
              )
              if (foregroundHashRef.current === record.txHash)
                fail({
                  title: 'Deposit was orphaned',
                  message:
                    'The indexed chain event is no longer canonical. Contact support before retrying.',
                  terminal: true,
                })
              return
            }
            if (deposit.status === 'USER_NOT_EXIST') {
              clearPendingDeposit(record.txHash!)
              trackingHashesRef.current.delete(record.txHash!)
              notifyDepositOutcome(
                record,
                'user-not-exist',
                'Deposit account was not found',
                'The deposit could not be mapped to your DORA account. Contact support with the transaction hash.',
                NotificationStatus.ERROR,
              )
              if (foregroundHashRef.current === record.txHash)
                fail({
                  title: 'DORA account was not found',
                  message:
                    'The deposit could not be mapped to your account. Contact support with the transaction hash.',
                  terminal: true,
                })
              return
            }
            if (deposit.status === 'DUST') {
              clearPendingDeposit(record.txHash!)
              trackingHashesRef.current.delete(record.txHash!)
              notifyDepositOutcome(
                record,
                'dust',
                'Deposit is below the minimum',
                `Deposits below ${config.minimumUsdc} USDC cannot be credited automatically.`,
                NotificationStatus.ERROR,
              )
              if (foregroundHashRef.current === record.txHash)
                fail({
                  title: 'Deposit is below the supported minimum',
                  message: `Deposits below ${config.minimumUsdc} USDC cannot be credited automatically.`,
                  terminal: true,
                })
              return
            }
          }
        } catch {
          Sentry.addBreadcrumb({
            category: 'web3-deposit',
            level: 'warning',
            message: 'Deposit reconciliation attempt failed',
          })
        }

        // Back off with jitter while indexing catches up, keeping concurrent deposits from polling in sync.
        const cap = Math.min(
          config.depositPollMaxMs,
          config.depositPollInitialMs * 2 ** Math.min(attempt, 8),
        )
        const waitMs =
          cap <= config.depositPollInitialMs
            ? config.depositPollInitialMs
            : config.depositPollInitialMs + Math.random() * (cap - config.depositPollInitialMs)
        attempt += 1
        await delay(waitMs)
      }
    },
    [config, fail, findDepositByHash, notifyDepositOutcome, queryClient],
  )

  const track = useCallback(
    async (record: PendingDepositRecord, selectedProvider?: EIP1193Provider | null) => {
      if (!record.txHash || trackingHashesRef.current.has(record.txHash)) return
      trackingHashesRef.current.add(record.txHash)
      const generation = trackerGenerationRef.current
      const isCurrent = () => mountedRef.current && trackerGenerationRef.current === generation
      const isForeground = () => isCurrent() && foregroundHashRef.current === record.txHash

      try {
        if (record.phase === 'transaction-submitted') {
          // Keep the saved transaction for resumption when a wallet can check its receipt.
          if (!selectedProvider) {
            if (isForeground()) setPhase('tx-pending')
            trackingHashesRef.current.delete(record.txHash)
            return
          }
          if (isForeground()) setPhase('tx-pending')
          const { publicClient } = createMetaMaskClients(selectedProvider, config.chainId)
          const receipt = await publicClient.waitForTransactionReceipt({
            hash: record.txHash,
            confirmations: config.requiredConfirmations,
            timeout: 180_000,
          })
          if (!isCurrent()) return
          if (receipt.status !== 'success') {
            clearPendingDeposit(record.txHash!)
            trackingHashesRef.current.delete(record.txHash!)
            notifyDepositOutcome(
              record,
              'reverted',
              'Deposit transaction failed',
              'The transaction reverted. USDC was not deposited, but ETH gas may have been spent.',
              NotificationStatus.ERROR,
            )
            if (isForeground())
              fail({
                title: 'Deposit transaction failed',
                message:
                  'The transaction reverted. USDC was not deposited, but ETH gas may have been spent.',
                terminal: true,
              })
            return
          }
          record = { ...record, phase: 'credit-pending' }
          savePendingDeposit(record)
        }

        if (!isCurrent()) return
        if (isForeground()) setPhase('credit-pending')
        await reconcileDeposit(record, generation)
      } catch (trackingError) {
        if (!isCurrent()) return
        trackingHashesRef.current.delete(record.txHash!)
        if (isForeground())
          fail(
            {
              title: 'Deposit status is temporarily unavailable',
              message:
                'Your transaction is still saved. Resume tracking instead of submitting again.',
            },
            trackingError,
          )
      }
    },
    [config.chainId, config.requiredConfirmations, fail, notifyDepositOutcome, reconcileDeposit],
  )

  useEffect(() => {
    mountedRef.current = true
    if (!withdrawalsAvailable) return
    let unsubscribe: () => void = () => undefined
    let cancelled = false
    const pendingTransaction = loadPendingDeposits().find(
      (pending) => pending.userId === user?.id && pending.phase === 'transaction-submitted',
    )
    if (pendingTransaction?.txHash) {
      foregroundHashRef.current = pendingTransaction.txHash
      setTxHash(pendingTransaction.txHash)
      setPhase('tx-pending')
    }

    void discoverMetaMask().then(async (discoveredProvider) => {
      if (cancelled) return
      setWalletAvailable(Boolean(discoveredProvider))
      setProvider(discoveredProvider)
      if (!discoveredProvider) {
        for (const pending of loadPendingDeposits()) {
          if (pending.userId === user?.id) void track(pending, null)
        }
        return
      }

      unsubscribe = subscribeToMetaMask(discoveredProvider, {
        accountsChanged: (accounts) => {
          const nextAccount = accounts[0] ?? null
          void settleWalletState(discoveredProvider, nextAccount).catch((walletError) =>
            fail({
              title: 'Wallet state could not be refreshed',
              message: normalizeWalletError(walletError),
            }),
          )
        },
        chainChanged: () => {
          void getConnectedAccounts(discoveredProvider).then((accounts) =>
            settleWalletState(discoveredProvider, accounts[0] ?? null),
          )
        },
        disconnect: () => {
          clearWalletConnection()
        },
      })

      const accounts = await getConnectedAccounts(discoveredProvider)
      await settleWalletState(discoveredProvider, accounts[0] ?? null)
      for (const pending of loadPendingDeposits()) {
        if (pending.userId === user?.id) void track(pending, discoveredProvider)
      }
    })

    return () => {
      cancelled = true
      mountedRef.current = false
      // Invalidate outstanding receipt waits and reconciliation loops before the next account setup.
      trackerGenerationRef.current += 1
      trackingHashesRef.current.clear()
      unsubscribe()
    }
  }, [clearWalletConnection, withdrawalsAvailable, fail, settleWalletState, track, user?.id])

  const connect = useCallback(async () => {
    if (!provider) {
      setWalletAvailable(false)
      return
    }
    try {
      setError(null)
      const connectedAccount = await connectMetaMask(provider)
      await settleWalletState(provider, connectedAccount)
    } catch (walletError) {
      fail({ title: 'Wallet connection failed', message: normalizeWalletError(walletError) })
    }
  }, [fail, provider, settleWalletState])

  const disconnect = useCallback(async () => {
    if (!provider || disconnecting) return
    try {
      setDisconnecting(true)
      await disconnectMetaMask(provider)
      if (mountedRef.current) clearWalletConnection()
    } catch (walletError) {
      fail({
        title: 'Wallet could not be disconnected',
        message: `${normalizeWalletError(walletError)} You can also disconnect localhost from MetaMask's connected sites.`,
      })
    } finally {
      if (mountedRef.current) setDisconnecting(false)
    }
  }, [clearWalletConnection, disconnecting, fail, provider])

  const switchNetwork = useCallback(async () => {
    if (!provider) return
    try {
      setError(null)
      await switchProviderChain(provider, config.chainId, config.rpcUrl)
      const accounts = await getConnectedAccounts(provider)
      await settleWalletState(provider, accounts[0] ?? null)
    } catch (walletError) {
      fail({ title: 'Network switch failed', message: normalizeWalletError(walletError) })
    }
  }, [config.chainId, config.rpcUrl, fail, provider, settleWalletState])

  const amountValidation = useMemo(
    () => validateUsdcAmount(amount, config.minimumUsdc, usdcBalance ?? undefined),
    [amount, config.minimumUsdc, usdcBalance],
  )
  const amountError = amountValidation.valid
    ? null
    : amountValidation.reason === 'below-minimum'
      ? `Minimum deposit is ${config.minimumUsdc} USDC.`
      : amountValidation.reason === 'insufficient-usdc'
        ? 'Amount exceeds your USDC wallet balance.'
        : amount
          ? 'Enter a plain decimal amount with no more than 6 decimals.'
          : null

  const submit = useCallback(async () => {
    if (
      !available ||
      !provider ||
      !account ||
      !user ||
      phase !== 'deposit-form' ||
      submissionInFlightRef.current
    )
      return
    const validatedAmount = validateUsdcAmount(amount, config.minimumUsdc, usdcBalance ?? undefined)
    if (!validatedAmount.valid) return
    const pendingTransaction = loadPendingDeposits().find(
      (pending) =>
        pending.userId === user.id &&
        addressesEqual(pending.walletAddress, account) &&
        pending.chainId === config.chainId &&
        pending.phase === 'transaction-submitted',
    )
    if (pendingTransaction?.txHash) {
      foregroundHashRef.current = pendingTransaction.txHash
      setTxHash(pendingTransaction.txHash)
      setError(null)
      setPhase('tx-pending')
      void track(pendingTransaction, provider)
      return
    }
    // Lock synchronously so repeated clicks cannot open parallel wallet signing requests.
    submissionInFlightRef.current = true

    const record: PendingDepositRecord = {
      version: 1,
      userId: user.id,
      walletAddress: account,
      chainId: config.chainId,
      quantity: amount,
      phase: 'instructions',
      createdAt: new Date().toISOString(),
    }
    foregroundHashRef.current = null
    setTxHash(null)
    setError(null)
    setEstimatedMaximumFee(null)
    setPhase('preparing')
    let submissionStage: 'preparing' | 'permit' | 'transaction' = 'preparing'

    try {
      const currentAccounts = await getConnectedAccounts(provider)
      const currentChainId = await getProviderChainId(provider)
      if (!currentAccounts[0] || !addressesEqual(currentAccounts[0], account)) {
        throw new Error('Connected MetaMask account changed before signing')
      }
      if (currentChainId !== config.chainId)
        throw new Error('MetaMask network changed before signing')

      const { publicClient, walletClient } = createMetaMaskClients(provider, config.chainId)
      const usdcAddress = getAddress(config.usdcAddress)
      const vaultAddress = getAddress(config.vaultAddress)
      const nonce = await publicClient.readContract({
        address: usdcAddress,
        abi: erc20DepositAbi,
        functionName: 'nonces',
        args: [account],
      })
      const response = await getDepositInstructions({
        quantity: amount,
        ownerAddress: account,
        nonce,
      })
      const instruction = validateDepositInstructions({
        response,
        userId: user.id,
        ownerAddress: account,
        quantity: amount,
        nonce,
        chainId: config.chainId,
        usdcAddress,
        vaultAddress,
        expirySafetySeconds: config.permitExpirySafetySeconds,
      })

      setPhase('wallet-confirm-permit')
      submissionStage = 'permit'
      const signature = await signPermitTypedData(provider, account, instruction.approval)
      // Wallet approval may take time; recheck the permit deadline before preparing the transaction.
      validateDepositInstructions({
        response,
        userId: user.id,
        ownerAddress: account,
        quantity: amount,
        nonce,
        chainId: config.chainId,
        usdcAddress,
        vaultAddress,
        expirySafetySeconds: config.permitExpirySafetySeconds,
      })
      const data = encodeDepositCalldata(instruction, signature)
      const gas = await publicClient.estimateGas({ account, to: vaultAddress, data, value: 0n })
      const fees = await publicClient.estimateFeesPerGas()
      const maximumFeePerGas = fees.maxFeePerGas ?? fees.gasPrice
      if (!maximumFeePerGas)
        throw new Error(`${config.networkName} did not return an EIP-1559 maximum fee`)
      const maximumFee = gas * maximumFeePerGas
      // Ceiling division preserves the full safety buffer when converting basis points to integer wei.
      const bufferedMaximumFee =
        (maximumFee * BigInt(10_000 + config.gasFeeSafetyBps) + 9_999n) / 10_000n
      const freshEthBalance = await publicClient.getBalance({ address: account })
      setEthBalance(freshEthBalance)
      setEstimatedMaximumFee(bufferedMaximumFee)
      if (freshEthBalance < bufferedMaximumFee) {
        throw new Error(
          `Insufficient ETH for the maximum fee plus buffer (${formatEther(bufferedMaximumFee)} ETH)`,
        )
      }

      const finalAccounts = await getConnectedAccounts(provider)
      const finalChainId = await getProviderChainId(provider)
      if (!finalAccounts[0] || !addressesEqual(finalAccounts[0], account)) {
        throw new Error('Connected MetaMask account changed before transaction submission')
      }
      if (finalChainId !== config.chainId)
        throw new Error('MetaMask network changed before submission')

      setPhase('wallet-confirm-tx')
      submissionStage = 'transaction'
      const hash = await walletClient.sendTransaction({
        account,
        to: vaultAddress,
        data,
        value: 0n,
        gas,
        maxFeePerGas: maximumFeePerGas,
        maxPriorityFeePerGas: fees.maxPriorityFeePerGas,
      })
      const pending: PendingDepositRecord = {
        ...record,
        phase: 'transaction-submitted',
        txHash: hash,
      }
      // Persist the broadcast hash before tracking so a reload can resume without another deposit.
      savePendingDeposit(pending)
      foregroundHashRef.current = hash
      setTxHash(hash)
      Sentry.addBreadcrumb({ category: 'web3-deposit', message: 'Deposit transaction submitted' })
      void track(pending, provider)
    } catch (submissionError) {
      fail(
        {
          title:
            submissionStage === 'permit'
              ? 'Permit was not signed'
              : submissionStage === 'transaction'
                ? 'Deposit was not submitted'
                : 'Deposit could not be prepared',
          message: normalizeWalletError(submissionError),
        },
        submissionError,
      )
    } finally {
      submissionInFlightRef.current = false
    }
  }, [account, amount, available, config, fail, phase, provider, track, user, usdcBalance])

  const retry = useCallback(() => {
    setError(null)
    if (!account) setPhase('disconnected')
    else if (chainId !== config.chainId) setPhase('wrong-network')
    else setPhase('deposit-form')
  }, [account, chainId, config.chainId])

  const resume = useCallback(async () => {
    const pending = loadPendingDeposits().find(
      (record) => record.txHash === foregroundHashRef.current,
    )
    if (!pending?.txHash) {
      retry()
      return
    }
    setError(null)
    await track(pending, provider)
  }, [provider, retry, track])

  const reset = useCallback(() => {
    if (submissionInFlightRef.current || phase === 'tx-pending') return
    foregroundHashRef.current = null
    setAmount('')
    setTxHash(null)
    setEstimatedMaximumFee(null)
    setError(null)
    setUsdcBalance(null)
    retry()
    if (provider && account && chainId === config.chainId) {
      void refreshBalances(provider, account).catch((walletError) =>
        fail({
          title: 'Wallet balance could not be refreshed',
          message: normalizeWalletError(walletError),
        }),
      )
    }
  }, [account, chainId, config.chainId, fail, phase, provider, refreshBalances, retry])

  const openDeposit = useCallback(() => {
    if (!available) return
    // Opening Deposit starts a fresh form once the wallet transaction is confirmed.
    // The previous transaction keeps reconciling independently in the background.
    if (phase === 'credit-pending' || phase === 'completed') reset()
    setOpen(true)
  }, [available, phase, reset])

  const contextValue = useMemo(
    () => ({ available, withdrawalsAvailable, openDeposit }),
    [available, withdrawalsAvailable, openDeposit],
  )
  const modalModel: DepositModalModel = {
    open,
    setOpen,
    phase,
    account,
    chainId,
    walletAvailable,
    disconnecting,
    usdcBalance,
    ethBalance,
    estimatedMaximumFee,
    amount,
    setAmount,
    amountError,
    txHash,
    error,
    connect,
    disconnect,
    switchNetwork,
    submit,
    retry,
    resume,
    reset,
  }

  return (
    <DepositContext.Provider value={contextValue}>
      {children}
      {available ? <DepositModal model={modalModel} /> : null}
    </DepositContext.Provider>
  )
}

export const useDeposit = () => useContext(DepositContext)
